import React, { forwardRef, useEffect, useState } from 'react';
import { CustomModal, LimitBanner } from '@components';
import {
    Box,
    FormControl,
    InputAdornment,
    InputLabel,
    OutlinedInput
} from '@mui/material';
import { useDispatch } from 'react-redux';
import { apiSaveBidOrder } from '@store';
import { showToast } from '@utils';
import useLimitCheck from '@hooks/useLimitCheck';
import axios from 'axios';

const Sell = forwardRef(({ handleCloseModal, selectedRow }, ref) => {
    const dispatch = useDispatch();

    const isPartialFillOk = selectedRow?.option === 'POK';
    const maxVolume = selectedRow?.quantity || 0;

    // Volume is editable when partial fill is allowed, otherwise locked to the full quantity
    const [volume, setVolume] = useState(maxVolume);

    useEffect(() => {
        setVolume(selectedRow?.quantity || 0);
    }, [selectedRow?.uuid, selectedRow?.quantity]);

    const handleVolumeChange = (e) => {
        const raw = e.target.value;
        if (raw === '') {
            setVolume('');
            return;
        }
        if (!/^\d*\.?\d{0,2}$/.test(raw)) return;
        let val = parseFloat(raw);
        if (isNaN(val)) return;
        if (val > maxVolume) {
            setVolume(maxVolume.toString());
            return;
        }
        setVolume(raw);
    };

    const state = {
        uuid: selectedRow?.uuid || '',
        contract: selectedRow?.contract || '',
        owner: selectedRow?.owner || '',
        price: selectedRow?.price || '',
        volume: volume || 0,
        buyer: '',
        seller: selectedRow?.owner || '',
        location: selectedRow?.location || '',
        currency: selectedRow?.currency || '',
        ttl: selectedRow?.ttl || '',
        type: 'buy',
        option: selectedRow?.option || '',
        step: 0.1,
        minVolume: 0,
        maxVolume,
        valid_until: selectedRow?.valid_until || ''
    };

    // Fetch notional value
    const [notionalValue, setNotionalValue] = useState(0);

    useEffect(() => {
        const fetchNotionalValue = async () => {
            if (!selectedRow?.contract || !selectedRow?.price || !volume)
                return;
            try {
                const response = await axios.get(
                    `/api/limits/nominalvalue?sym=${selectedRow.contract}&price=${selectedRow.price}&volume=${volume}`
                );
                setNotionalValue(response?.data?.nominal_value || 0);
            } catch (err) {
                console.error('Failed to fetch nominal value:', err);
            }
        };
        fetchNotionalValue();
    }, [selectedRow?.contract, selectedRow?.price, volume]);

    // Add submitting state
    const [submitting, setSubmitting] = useState(false);

    // Use limit check
    const limitCheck = useLimitCheck({
        action: 'sell',
        currency: selectedRow?.currency?.toLowerCase(),
        notionalValue,
        volume
    });

    const handleSell = async (e) => {
        e.preventDefault();
        if (submitting || limitCheck.isDisabled) return;
        setSubmitting(true);
        const resp = await dispatch(apiSaveBidOrder(state));
        if (resp?.meta?.requestStatus === 'fulfilled') {
            showToast('success', 'Contract Sold Successfully');
            handleCloseModal();
        } else if (resp?.meta?.requestStatus === 'rejected') {
            showToast('error', resp?.payload || 'Error Selling Contract');
            setSubmitting(false);
        }
    };

    return (
        <CustomModal
            handleClose={handleCloseModal}
            handleSubmit={handleSell}
            modalTitle={`Sell Contract`}
            modalTitleColor={'#EB6969'}
            buttonText={'Sell'}
            className="modal-button-danger"
            disabled={
                submitting || limitCheck.isDisabled || limitCheck.isLoading
            } // now disabled when submitting or limit check fails or loading
            ref={ref}
        >
            <LimitBanner messages={limitCheck.messages} />
            <Box className="flex flex-col gap-3 mt-2">
                <FormControl>
                    <InputLabel id="contractLabel">Contract</InputLabel>
                    <OutlinedInput
                        id="contract"
                        label="contract"
                        name="contract"
                        value={selectedRow?.contract}
                        disabled
                    />
                </FormControl>
                <FormControl fullWidth variant="outlined">
                    <InputLabel
                        id="contractLabel"
                        sx={{ bgcolor: '#F5F5F5', p: '0 3px' }}
                    >
                        Volume
                    </InputLabel>
                    <OutlinedInput
                        id="volume"
                        label="Volume"
                        name="quantity"
                        type="number"
                        value={volume}
                        onChange={handleVolumeChange}
                        inputProps={{ step: 0.01, min: 0, max: maxVolume }}
                        required
                        disabled={!isPartialFillOk}
                        endAdornment={
                            <InputAdornment position="start">MW</InputAdornment>
                        }
                    />
                </FormControl>
                <FormControl>
                    <InputLabel id="priceLabel">Price</InputLabel>
                    <OutlinedInput
                        id="price"
                        label="price"
                        name="price"
                        value={selectedRow?.price}
                        disabled
                    />
                </FormControl>
                <FormControl>
                    <InputLabel id="sellerLabel">Seller</InputLabel>
                    <OutlinedInput
                        id="seller"
                        label="seller"
                        name="owner"
                        value={selectedRow?.owner}
                        disabled
                    />
                </FormControl>
                <FormControl>
                    <InputLabel id="locationLabel">Location</InputLabel>
                    <OutlinedInput
                        id="location"
                        label="location"
                        name="location"
                        value={selectedRow?.location}
                        disabled
                    />
                </FormControl>
                <FormControl>
                    <InputLabel id="currencyLabel">Currency</InputLabel>
                    <OutlinedInput
                        id="currency"
                        label="currency"
                        name="currency"
                        value={selectedRow?.currency}
                        disabled
                    />
                </FormControl>
                <FormControl>
                    <InputLabel id="validUntilLabel">Valid Until</InputLabel>
                    <OutlinedInput
                        id="validUntil"
                        label="validUntil"
                        name="valid_until"
                        value={selectedRow?.valid_until}
                        disabled
                    />
                </FormControl>
            </Box>
        </CustomModal>
    );
});

export default Sell;
